import React from 'react';
import { FormikErrors } from 'formik';
import { AuthSecurity, Button, DOBInput, FieldWrapper, Form, FormError, Identity, LayoutSplit } from 'components';
import { RecoveryPinInput } from 'components/Input';
import { Message } from 'components/Message';
import { Appearance } from 'design/types';
import { AuthContainer, AuthContent, AuthContentInner, AuthLegalText } from '../Auth/styles';

interface IFormikSignUpSecurityValues {
  dateOfBirth: string;
  recoveryPin: string;
  identity: string;
}

interface ISignUpSecurityProps {
  authHeader: JSX.Element;
  onSubmit: () => void;
  formId: string;
  errors: FormikErrors<IFormikSignUpSecurityValues>;
  validateField: (field: 'identity' | 'recoveryPin' | 'dateOfBirth') => boolean;
  isLoading: boolean;
  onNewForm: () => void;
  getFieldProps: (fieldName: string) => any;
  setFieldValue: (fieldName: string, value: any) => void;
  isValid: boolean;
  dirty: boolean;
  values: Record<string, any>;
  errorMessages: string[];
  handleBlur: (e: Event) => void;
}

const formatError = (error: string) => {
  if (error.toLowerCase().trim() === 'identity already exists') {
    return 'An account with this mobile number already exists.';
  }

  if (error.toLowerCase().trim().endsWith('recovery_pin must be a number string')) {
    return 'Your recovery pin must be 4 digit number.';
  }

  return null;
};

const SignUpSecurity = ({ authHeader, onNewForm, formId, onSubmit, errors, validateField, isLoading, getFieldProps, setFieldValue, errorMessages, isValid, dirty, handleBlur }: ISignUpSecurityProps) => (
  <LayoutSplit
    left={
      <AuthContainer>
        <AuthContent data-cy="create-account-security">
          <AuthContentInner>
            {authHeader}
            <Message appearance={Appearance.WARNING} message="Please remember to write down your recovery pin code and keep it in a safe place. Without it, you will not be able to recover your account." />
            <Form onNewForm={onNewForm} formId={formId} onSubmit={onSubmit}>
              <FieldWrapper data-cy="identity-field" label="Mobile Number" message={validateField('identity') ? errors.identity : ''} hasError={validateField('identity')}>
                <Identity
                  disabled={isLoading}
                  id="identity"
                  onAccept={(val: string) => {
                    setFieldValue('identity', val);
                  }}
                  onBlur={handleBlur}
                  placeholder="Mobile Number"
                  type="text"
                  inputMode="numeric"
                  phoneOnly
                />
              </FieldWrapper>
              <FieldWrapper data-cy="date-of-birth-field" label="Date of Birth" message={validateField('dateOfBirth') ? errors.dateOfBirth : ''} hasError={validateField('dateOfBirth')}>
                <DOBInput id="dateOfBirth" onBlur={handleBlur} disabled={isLoading} type="text" placeholder="MM-DD-YYYY" {...getFieldProps('dateOfBirth')} />
              </FieldWrapper>
              <FieldWrapper data-cy="recovery-pin-field" label="Recovery Pin" message={validateField('recoveryPin') ? errors.recoveryPin : ''} hasError={validateField('recoveryPin')}>
                <RecoveryPinInput id="recoveryPin" disabled={isLoading} type="text" placeholder="4 digit pin code" {...getFieldProps('recoveryPin')} onBlur={handleBlur} />
              </FieldWrapper>

              {errorMessages &&
                errorMessages.map((error: string, index: number) => (
                  <FormError data-cy="form-error" key={`error-message-${index}`}>
                    {formatError(error)}
                  </FormError>
                ))}

              <Button data-cy="submit-button" type="submit" label="Continue" disabled={isLoading || !(isValid && dirty)} isProcessing={isLoading} />
            </Form>
            <AuthLegalText>By submitting, you confirm that you are authorized to use the number entered and agree to receive SMS texts to verify you own the number. Carrier fees may apply.</AuthLegalText>
          </AuthContentInner>
        </AuthContent>
      </AuthContainer>
    }
    right={<AuthSecurity />}
  />
);

export default SignUpSecurity;
